// FRU-23847 (ANGULAR-7EE): a phone that drops its connection (out of signal, or moving between Wi-Fi and cellular) closes the
// page's requests in flight, and they fail with status 0. Cloudflare logs those as 499 (the browser closed the request), not as
// a server error, so reporting each one as a Sentry exception buried the real ones (about 40 to 50 a day after FRU-23258).
// This records the signs of a dropped connection so a status-0 failure can be told apart from one with the connection working.

export type ConnectionDropReason = 'offline' | 'network_change' | 'failed_together';

const signals = { offline: 0, change: 0 };
// Rate finder requests that got no answer, in the order they were noted: two close together are one incident.
let failures: { at: number, order: number }[] = [];
let noted = 0;
let watching = false;

function isEventTarget(value: unknown): boolean {
  return !!value && typeof EventTarget !== 'undefined' && value instanceof EventTarget;
}

/** Record connection signals from the given window and navigator. watchConnectionDrops() wires the real ones once. */
export function listenForConnectionDrops(win: EventTarget, nav: any): void {
  try {
    win.addEventListener('offline', () => { signals.offline = Date.now(); }, true);
    const connection = nav ? (nav.connection || nav.mozConnection || nav.webkitConnection) : null;
    // FRU-24097 (ANGULAR-7EN): some Android browsers expose a connection object that has an addEventListener but isn't an
    // event target. Calling it throws, and zone.js reports that to Angular's error handler even though it's caught here.
    if (isEventTarget(connection)) {
      // Network Information API (Chrome on Android). `change` also fires for speed estimates (rtt, downlink), so only a change
      // of connection type counts: the phone switched network, such as Wi-Fi to cellular. Where no type is exposed, none counts.
      let lastType = typeof connection.type === 'string' ? connection.type : '';
      connection.addEventListener('change', () => {
        const type = typeof connection.type === 'string' ? connection.type : '';
        if (type && lastType && type !== lastType) { signals.change = Date.now(); }
        lastType = type || lastType;
      });
    }
  } catch (e) { /* connection signals are best effort */ }
}

/** Start recording connection signals. Safe to call more than once; only the first call adds listeners. */
export function watchConnectionDrops(): void {
  if (watching || typeof window === 'undefined') { return; }
  watching = true;
  listenForConnectionDrops(window, typeof navigator !== 'undefined' ? navigator : null);
}

/** A rate finder request got no answer (status 0) at this time. Returns its place in order, for connectionDroppedAround. */
export function noteRequestWithoutAnswer(at: number = Date.now()): number {
  noted++;
  failures.push({ at, order: noted });
  if (failures.length > 20) { failures = failures.slice(-20); }
  return noted;
}

/**
 * Why a request that got no answer at `at` looks like a dropped connection: the browser says it is offline now, or it went offline
 * or switched network within `windowMs` before the failure or since. For requests that got no answer together, only the first of
 * the group is reported: an outage on our side and a dropped phone look the same, so one report keeps the signal and the rest are
 * breadcrumbs. Null when the failure should be reported.
 */
export function connectionDroppedAround(
  at: number,
  windowMs: number,
  nav: any = typeof navigator !== 'undefined' ? navigator : null,
  order: number = Number.MAX_SAFE_INTEGER,
): ConnectionDropReason | null {
  try {
    if (nav && nav.onLine === false) { return 'offline'; }
  } catch (e) { /* fall through to the recorded signals */ }
  const from = at - windowMs;
  if (signals.offline && signals.offline >= from) { return 'offline'; }
  if (signals.change && signals.change >= from) { return 'network_change'; }
  // Another request got no answer shortly before this one (noted earlier, even in the same millisecond): that one reports.
  for (const f of failures) {
    if (f.order < order && at - f.at <= windowMs) { return 'failed_together'; }
  }
  return null;
}

/** Test-only reset. */
export function resetConnectionDropSignals(): void {
  signals.offline = 0;
  signals.change = 0;
  failures = [];
  noted = 0;
}
